import DynaRouter from 'utils/constants/abi/DynaRouter.json';
import {
  useReadContract,
  usePublicClient,
  useWriteContract,
  useSimulateContract,
} from 'wagmi';
import { QueryOptions } from 'utils/useQueryUtils';
import {
  ETH_AVERAGE_BLOCK_TIME,
  ETH_ZERO_ADDRESS,
} from 'utils/constants/ethConst';
import { useQuery } from '@tanstack/react-query';
import { Abi, parseAbi } from 'viem';
import {
  useEstimateContractGas,
  useQueryEstimateContractGas,
  addBuffer,
} from 'utils/hooks/useEstimateContractGas';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import {
  DynaRoutePreviewerABI,
  getRoutePreviewerAddress,
  VaultDynaZapperABI,
} from 'sfi-router-sdk';
import { DynaVaultABI } from 'sfi-vault-sdk';
import {
  getNativePreviewArgs,
  getNativeSwapParameters,
} from 'structure/pv2/vault/utils/nativeDeposit';
import { encodeNativeWithdrawalSwapData } from 'structure/pv2/vault/utils/nativeWithdrawal';

interface UsePreviewSwapParams extends QueryOptions {
  contractAddress: `0x${string}`;
  from: `0x${string}`;
  chainId?: number;
  tokenIn: `0x${string}`;
  amountIn: string;
  tokenOut: `0x${string}`;
}

const parsePreviewSwapResult = (data: unknown) => {
  const result = data as readonly [
    bigint,
    `0x${string}`,
    readonly `0x${string}`[],
  ];

  return {
    amountOut: result[0],
    selectedRouter: result[1],
    swapData: result[2],
  };
};

export const usePreviewSwap = (params: UsePreviewSwapParams) => {
  const { from, contractAddress, chainId, tokenIn, amountIn, tokenOut } =
    params;
  const useQueryOptions = {
    queryKey: [
      'previewSwap',
      contractAddress,
      chainId,
      tokenIn,
      amountIn,
      tokenOut,
    ],
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    ...params.query,
  };

  const gas = useEstimateContractGas(
    {
      account: from,
      address: contractAddress,
      abi: DynaRouter as Abi,
      functionName: 'previewSwap',
      chainId,
      args: [tokenIn, BigInt(amountIn), tokenOut],
      query: useQueryOptions,
    },
    [contractAddress, chainId, from, tokenIn, amountIn, tokenOut]
  );

  const extraParams = {
    overrides: {
      gasLimit: gas,
    },
  };

  const preview = useReadContract({
    account: from,
    abi: DynaRouter,
    functionName: 'previewSwap',
    chainId,
    // typescript workaround
    ...extraParams,
    address: contractAddress,
    args: [tokenIn, BigInt(amountIn), tokenOut],
    query: {
      ...useQueryOptions,
      select: parsePreviewSwapResult,
    },
  });

  const publicClient = usePublicClient({ chainId });
  const fetchFresh = async () => {
    if (!publicClient) {
      throw new Error('Public client not available');
    }

    const result = await publicClient.readContract({
      account: from,
      abi: DynaRouter as Abi,
      functionName: 'previewSwap',
      address: contractAddress,
      args: [tokenIn, BigInt(amountIn), tokenOut],
    });

    return parsePreviewSwapResult(result);
  };

  return { ...preview, fetchFresh };
};

type UseBestPreviewSwapParams = Omit<UsePreviewSwapParams, 'contractAddress'>;

export const useBestPreviewSwap = (params: UseBestPreviewSwapParams) => {
  const { from, chainId, tokenIn, amountIn, tokenOut } = params;
  const contractAddress = (getRoutePreviewerAddress(chainId ?? 0) ??
    ETH_ZERO_ADDRESS) as `0x${string}`;
  const args = getNativePreviewArgs({
    requester: from,
    tokenIn,
    amountIn,
    tokenOut,
  });
  const useQueryOptions = {
    queryKey: [
      'bestPreviewSwap',
      contractAddress,
      chainId,
      from,
      tokenIn,
      amountIn,
      tokenOut,
    ],
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    ...params.query,
    enabled:
      contractAddress !== ETH_ZERO_ADDRESS &&
      Boolean(from !== ETH_ZERO_ADDRESS) &&
      (params.query?.enabled ?? true),
  };

  const preview = useReadContract({
    account: from,
    abi: DynaRoutePreviewerABI,
    functionName: 'getBestPreviewFor',
    chainId,
    address: contractAddress,
    args,
    query: {
      ...useQueryOptions,
      select: parsePreviewSwapResult,
    },
  });

  const publicClient = usePublicClient({ chainId });
  const fetchFresh = async () => {
    if (!publicClient) {
      throw new Error('Public client not available');
    }

    const result = await publicClient.readContract({
      account: from,
      abi: DynaRoutePreviewerABI,
      functionName: 'getBestPreviewFor',
      address: contractAddress,
      args,
    });

    return parsePreviewSwapResult(result);
  };

  return { ...preview, fetchFresh };
};

export const usePreviewNativeSwap = useBestPreviewSwap;

interface UsePreviewNativeWithdrawalParams extends QueryOptions {
  contractAddress: `0x${string}`;
  from: `0x${string}`;
  chainId: number;
  vaultAddress: `0x${string}`;
  depositToken: `0x${string}`;
  amountIn: string;
  tokenOut: `0x${string}`;
}

export const usePreviewNativeWithdrawal = (
  params: UsePreviewNativeWithdrawalParams
) => {
  const {
    contractAddress,
    from,
    chainId,
    vaultAddress,
    depositToken,
    amountIn,
    tokenOut,
  } = params;
  const previewerAddress = (getRoutePreviewerAddress(chainId) ??
    ETH_ZERO_ADDRESS) as `0x${string}`;
  const publicClient = usePublicClient({ chainId });

  const fetchPreview = async () => {
    if (!publicClient) {
      throw new Error('Public client not available');
    }

    const assets = await publicClient.readContract({
      account: from,
      abi: DynaVaultABI,
      functionName: 'previewRedeem',
      address: vaultAddress,
      args: [BigInt(amountIn)],
    });
    const result = await publicClient.readContract({
      account: from,
      abi: DynaRoutePreviewerABI,
      functionName: 'getBestPreviewFor',
      address: previewerAddress,
      args: [from, depositToken, assets, tokenOut],
    });
    const preview = parsePreviewSwapResult(result);

    return {
      amountOut: preview.amountOut,
      selectedRouter: contractAddress,
      swapData: encodeNativeWithdrawalSwapData(
        preview.selectedRouter,
        preview.swapData
      ),
    };
  };

  const preview = useQuery({
    queryKey: [
      'previewNativeWithdrawal',
      contractAddress,
      previewerAddress,
      chainId,
      from,
      vaultAddress,
      depositToken,
      amountIn,
      tokenOut,
    ],
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    queryFn: fetchPreview,
    ...params.query,
    enabled:
      previewerAddress !== ETH_ZERO_ADDRESS &&
      from !== ETH_ZERO_ADDRESS &&
      (params.query?.enabled ?? true),
  });

  return { ...preview, fetchFresh: fetchPreview };
};

interface UseSwapParams extends QueryOptions {
  contractAddress: `0x${string}`;
  chainId?: number;
  tokenIn: `0x${string}`;
  amountIn: string;
  tokenOut: `0x${string}`; // vault address
  minAmountOut: string;
  routerAddress: `0x${string}`;
  from: `0x${string}`;
  to: `0x${string}`;
  swapData: readonly string[];
}

interface PrepareFreshSwapParams {
  amountIn: string;
  tokenOut: `0x${string}`;
  minAmountOut: string;
  routerAddress?: `0x${string}`;
  swapData: readonly string[];
}

export const useSwap = (params: UseSwapParams) => {
  const {
    contractAddress,
    chainId,
    tokenIn,
    amountIn,
    tokenOut,
    minAmountOut,
    routerAddress,
    from,
    to,
    swapData,
  } = params;
  const useQueryOptions = {
    queryKey: [
      'swap',
      contractAddress,
      chainId,
      tokenIn,
      amountIn,
      tokenOut,
      minAmountOut,
      routerAddress,
      from,
      to,
      swapData,
    ],
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    ...params.query,
  };

  const contractCallArgs = {
    abi: DynaRouter as Abi,
    functionName: 'swap',
    chainId,
    address: contractAddress,
    account: from,
    args: [tokenIn, amountIn, tokenOut, minAmountOut, to, swapData],
    query: useQueryOptions,
  } as const;

  const gasQuery = useQueryEstimateContractGas(contractCallArgs);
  const gas = gasQuery.data || BigInt(0);

  const prepare = useSimulateContract({
    ...contractCallArgs,
    query: {
      enabled:
        contractCallArgs.query.enabled &&
        gas > BigInt(0) &&
        gasQuery.isLoading !== true &&
        gasQuery.isError !== true,
    },
    gas,
  });

  const publicClient = usePublicClient({ chainId });
  const contract = useWriteContract();

  const prepareFresh = async (freshParams: PrepareFreshSwapParams) => {
    if (!publicClient) {
      throw new Error('Public client not available');
    }

    const request = {
      abi: DynaRouter as Abi,
      functionName: 'swap',
      address: freshParams.routerAddress ?? contractAddress,
      account: from,
      args: [
        tokenIn,
        BigInt(freshParams.amountIn),
        freshParams.tokenOut,
        BigInt(freshParams.minAmountOut),
        to,
        freshParams.swapData,
      ],
    } as const;

    const estimatedGas = await publicClient.estimateContractGas(request);

    return publicClient.simulateContract({
      ...request,
      gas: addBuffer(estimatedGas, chainId),
    });
  };

  return { prepare, prepareFresh, contract, gasQuery };
};

interface UseNativeSwapParams extends QueryOptions {
  chainId?: number;
  tokenIn: `0x${string}`;
  amountIn: string;
  tokenOut: `0x${string}`;
  minAmountOut: string;
  routerAddress: `0x${string}`;
  from: `0x${string}`;
  to: `0x${string}`;
  swapData: readonly string[];
}

interface PrepareFreshNativeSwapParams extends PrepareFreshSwapParams {
  routerAddress: `0x${string}`;
}

export const useNativeSwap = (params: UseNativeSwapParams) => {
  const {
    chainId,
    tokenIn,
    amountIn,
    tokenOut,
    minAmountOut,
    routerAddress,
    from,
    to,
    swapData,
  } = params;
  const transaction = getNativeSwapParameters({
    tokenIn,
    amountIn,
    tokenOut,
    minAmountOut,
    receiver: to,
    swapData,
  });
  const useQueryOptions = {
    queryKey: [
      'swapNative',
      chainId,
      tokenIn,
      amountIn,
      tokenOut,
      minAmountOut,
      routerAddress,
      from,
      to,
      swapData,
    ],
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    ...params.query,
  };

  const contractCallArgs = {
    abi: VaultDynaZapperABI,
    functionName: 'swapNative',
    chainId,
    address: routerAddress,
    account: from,
    args: transaction.args,
    value: transaction.value,
    query: useQueryOptions,
  } as const;

  const gasQuery = useQueryEstimateContractGas(contractCallArgs);
  const gas = gasQuery.data || BigInt(0);

  const prepare = useSimulateContract({
    ...contractCallArgs,
    query: {
      enabled:
        contractCallArgs.query.enabled &&
        gas > BigInt(0) &&
        gasQuery.isLoading !== true &&
        gasQuery.isError !== true,
    },
    gas,
  });

  const publicClient = usePublicClient({ chainId });
  const contract = useWriteContract();

  const prepareFresh = async (freshParams: PrepareFreshNativeSwapParams) => {
    if (!publicClient) {
      throw new Error('Public client not available');
    }

    const freshTransaction = getNativeSwapParameters({
      tokenIn,
      amountIn: freshParams.amountIn,
      tokenOut: freshParams.tokenOut,
      minAmountOut: freshParams.minAmountOut,
      receiver: to,
      swapData: freshParams.swapData,
    });
    const request = {
      abi: VaultDynaZapperABI,
      functionName: 'swapNative',
      address: freshParams.routerAddress,
      account: from,
      args: freshTransaction.args,
      value: freshTransaction.value,
    } as const;

    const estimatedGas = await publicClient.estimateContractGas(request);

    return publicClient.simulateContract({
      ...request,
      gas: addBuffer(estimatedGas, chainId),
    });
  };

  return { prepare, prepareFresh, contract, gasQuery };
};

interface UseSpenderAllowanceParams extends QueryOptions {
  chainId: number;
  account: `0x${string}`;
  contractAddress: `0x${string}`;
  tokenAddress: `0x${string}`;
}

export const useSpenderAllowance = (params: UseSpenderAllowanceParams) => {
  const { contractAddress, account, tokenAddress, chainId } = params;
  const useQueryOptions = {
    queryKey: queryKeys.spenderAllowance({
      contractAddress,
      ownerAddress: account,
      tokenAddress,
      chainId,
    }),
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    ...params.query,
  };

  return useReadContract({
    abi: DynaRouter,
    address: contractAddress,
    functionName: 'spenderAllowance',
    account,
    chainId,
    args: [tokenAddress],
    query: useQueryOptions,
  });
};

const vaultZapperAbi = parseAbi([
  'function whitelistRegistry() view returns (address)',
  'function checkVaultAllowed(address vaultAddress) view returns (bool)',
]);
const whitelistRegistryAbi = parseAbi([
  'function isTokenAllowedForUser(address token, address user) external view returns (bool)',
]);

interface UseIsTokenAllowedForUserParams extends QueryOptions {
  account: `0x${string}`;
  chainId: number;
  userAddress: `0x${string}`;
  tokenAddress: `0x${string}`;
  contractAddress: `0x${string}`;
}

export const useIsTokenAllowedForUser = (
  params: UseIsTokenAllowedForUserParams
) => {
  const {
    account,
    chainId,
    contractAddress,
    userAddress,
    tokenAddress,
    query,
  } = params;

  const useQueryOptions = {
    queryKey: [
      'IsTokenAllowedForUser',
      account,
      chainId,
      contractAddress,
      userAddress,
      tokenAddress,
    ],
    ...query,
  };

  const client = usePublicClient({ chainId });

  return useQuery({
    queryFn: async () => {
      if (!client) {
        throw new Error('Public client not available');
      }

      const whitelistRegistryAddress = await client.readContract({
        abi: vaultZapperAbi,
        account: account,
        address: contractAddress,
        functionName: 'whitelistRegistry',
        authorizationList: undefined,
      });

      return await client.readContract({
        abi: whitelistRegistryAbi,
        args: [tokenAddress, userAddress],
        account: account,
        address: whitelistRegistryAddress,
        functionName: 'isTokenAllowedForUser',
        authorizationList: undefined,
      });
    },
    ...useQueryOptions,
  });
};

export const usePermittedVault = (params: UseIsTokenAllowedForUserParams) => {
  const {
    account,
    chainId,
    contractAddress,
    userAddress,
    tokenAddress,
    query,
  } = params;

  const useQueryOptions = {
    queryKey: [
      'IsPermittedVault',
      account,
      chainId,
      contractAddress,
      userAddress,
      tokenAddress,
    ],
    ...query,
  };

  return useReadContract({
    abi: vaultZapperAbi,
    account: account,
    chainId: chainId,
    address: contractAddress,
    functionName: 'checkVaultAllowed',
    args: [tokenAddress],
    query: useQueryOptions,
  });
};
